import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * DataExplorer.jsx
 *
 * Proper-data version.
 *
 * No evidence-table parsing.
 * No qn-selection snapshot parsing.
 * No fallback to area_breakdown/topic_history.
 *
 * Required backend shape:
 *
 * stats.chart_builder = {
 *   current: [question-grain rows for current test],
 *   trend: [question-grain rows for current + up to 5 previous tests],
 *   section_history: [section-history rows for percentile/cutoff trend]
 * }
 *
 * User model:
 *   Measure [Attempt %]
 *   Of      [All sections]
 *           [All areas]    appears only after a specific section is selected
 *           [All topics]   appears only after a specific area is selected
 *   View    [Current Test / Trend]
 *   Type    [All / A / B / C]
 *
 * Chart grouping is inferred:
 * - All sections                         -> section bars
 * - Specific section + All areas          -> area bars
 * - Specific section + area + All topics  -> topic bars
 * - Specific topic                        -> selected-topic row / trend
 */

const SECTIONS = ['VARC', 'DILR', 'QA'];

const AREA_SHORT = {
  'Reading Comprehension': 'RC',
  'Verbal Ability': 'VA',
  'Verbal Reasoning': 'VA',
  'Data Interpretation': 'DI',
  'Logical Reasoning': 'LR',
  Numbers: 'Num',
  Arithmetic: 'Arith',
  Algebra: 'Alg',
  Geometry: 'Geo',
  'Modern Math': 'Mod',
};

const ALL_SECTIONS = '__ALL_SECTIONS__';
const ALL_AREAS = '__ALL_AREAS__';
const ALL_TOPICS = '__ALL_TOPICS__';
const ALL_TYPES = '__ALL_TYPES__';

const MEASURES = [
  { id: 'attempt_rate', label: 'Attempt Rate', subtitle: 'of {total} questions', help: 'questions attempted out of total' },
  { id: 'accuracy', label: 'Accuracy', subtitle: 'of {attempted} attempted', help: 'correct answers among attempted questions' },
  { id: 'conversion', label: 'Conversion', subtitle: 'of {total} total', help: 'correct answers out of all questions' },
  { id: 'cws', label: 'C / W / S', subtitle: 'correct · wrong · skipped', help: 'outcome breakdown' },
  { id: 'score_left', label: 'Score / Left', subtitle: 'scored vs available', help: 'marks scored vs marks left on table' },
  { id: 'leakage', label: 'Leakage', subtitle: 'marks lost', help: 'marks lost from wrong and skipped questions' },
  { id: 'time_min', label: 'Time', subtitle: 'minutes spent', help: 'time spent on questions' },
  { id: 'mpm', label: 'Marks/min', subtitle: 'marks per minute', help: 'efficiency: marks earned per minute spent' },
];

const css = `
.byc6{--ink:#111827;--muted:#667085;--border:#e5e7eb;--soft:#f8fafc;--blue:#2563eb;--green:#169b62;--red:#dc2626;--skip:#eef2f7;--skipline:#cbd5e1;font-family:'IBM Plex Sans',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:var(--ink);font-size:13px}
.byc6 *{box-sizing:border-box}
.byc6-shell{display:grid;grid-template-columns:250px 1fr;border:1px solid var(--border);border-radius:16px;background:#fff;overflow:hidden;box-shadow:0 8px 22px rgba(15,23,42,.06)}
.byc6-left{background:var(--soft);border-right:1px solid var(--border);padding:14px;display:flex;flex-direction:column;gap:12px}
.byc6-title{font-size:16px;font-weight:850;letter-spacing:-.02em}
.byc6-sub{font-size:11px;color:var(--muted);line-height:1.4;margin-top:2px}
.byc6-field{display:flex;flex-direction:column;gap:5px}
.byc6-label{font-size:10px;font-weight:850;text-transform:uppercase;letter-spacing:.05em;color:var(--muted)}
.byc6-select{width:100%;appearance:none;border:1px solid #d1d5db;border-radius:10px;background:#fff;color:#111827;font:inherit;font-weight:750;padding:9px 32px 9px 10px;cursor:pointer;background-image:linear-gradient(45deg,transparent 50%,#667085 50%),linear-gradient(135deg,#667085 50%,transparent 50%);background-position:calc(100% - 16px) 50%,calc(100% - 11px) 50%;background-size:5px 5px,5px 5px;background-repeat:no-repeat}
.byc6-select:disabled{opacity:.5;cursor:not-allowed;background:#f3f4f6}
.byc6-help{font-size:10px;color:var(--muted);line-height:1.35}
.byc6-path{font-size:11px;color:#374151;background:#fff;border:1px solid var(--border);border-radius:12px;padding:9px;line-height:1.45}
.byc6-context{font-size:12px;font-weight:700;color:#667085;background:#f8fafc;border-left:3px solid #2563eb;padding:7px 12px;border-radius:0 8px 8px 0;margin-bottom:14px}
.byc6-crumb{background:none;border:none;color:#2563eb;cursor:pointer;font:inherit;font-weight:700;padding:0;text-decoration:underline}
.byc6-crumb:hover{color:#1d4ed8}
.byc6-main{min-width:0;overflow:hidden;padding:16px 18px 18px}
.byc6-head{display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:14px}
.byc6-chart-title{font-size:17px;font-weight:850;letter-spacing:-.02em}
.byc6-chart-def{font-size:14px;color:#4B5563;line-height:1.5;margin-top:6px;font-weight:600}
.byc6-chart-scope{display:block;font-size:12px;color:var(--muted);font-weight:500;margin-top:2px}
.byc6-chart-sub{font-size:12px;color:var(--muted);line-height:1.45;margin-top:2px}
.byc6-pill{border:1px solid var(--border);background:var(--soft);border-radius:999px;padding:5px 9px;font-size:11px;font-weight:800;color:#475467;white-space:nowrap}
.byc6-view-toggle{display:flex;border:1px solid #d1d8e0;border-radius:999px;overflow:hidden;flex-shrink:0}
.byc6-view-toggle button{border:0;background:#fff;color:#374151;padding:6px 12px;font-size:12px;font-weight:800;cursor:pointer;font-family:inherit;white-space:nowrap}
.byc6-view-toggle button.active{background:#111827;color:#fff}
.byc6-sub-toggle{display:inline-flex;border:1px solid #d1d8e0;border-radius:999px;overflow:hidden;flex-shrink:0;margin-top:6px;margin-bottom:8px}
.byc6-sub-toggle button{border:0;background:#fff;color:#374151;padding:5px 14px;font-size:11px;font-weight:800;cursor:pointer;font-family:inherit;white-space:nowrap}
.byc6-sub-toggle button.active{background:#111827;color:#fff}
.byc6-row{display:grid;grid-template-columns:minmax(100px,180px) 1fr minmax(60px,88px);grid-template-areas:"name bar val";gap:10px;align-items:center;padding:9px 0;border-bottom:1px solid #f1f5f9}
.byc6-row:last-child{border-bottom:0}
.byc6-name{font-size:12px;font-weight:850;line-height:1.25;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;grid-area:name}
.byc6-meta{display:block;font-size:10px;color:var(--muted);font-weight:650;margin-top:2px}
.byc6-val{text-align:right;font-size:12px;font-weight:850;color:#344054;grid-area:val}
.byc6-bar,.byc6-stack{width:100%;min-width:0;grid-area:bar}
.byc6-bar{height:24px;border-radius:8px;background:#f0f4f8;overflow:hidden;display:flex}
.byc6-fill{height:100%;background:#2563eb;border-radius:0 7px 7px 0;min-width:2px}
.byc6-fill.neg{background:var(--red)}
.byc6-fill.green{background:#169b62}
.byc6-fill.red{background:#dc2626}
.byc6-fill.orange{background:#f97316}
.byc6-stack{height:24px;border-radius:8px;overflow:hidden;display:flex;background:#f3f4f6}
.byc6-seg{height:100%;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:900;min-width:0;overflow:hidden}
.byc6-green{background:var(--green);color:#fff}
.byc6-red{background:var(--red);color:#fff}
.byc6-skip{background:repeating-linear-gradient(135deg,var(--skip) 0 5px,var(--skipline) 5px 7px,var(--skip) 7px 11px);color:#334155}
.byc6-score{background:var(--blue);color:#fff}
.byc6-neg{background:var(--red);color:#fff}
.byc6-leftmarks{background:repeating-linear-gradient(135deg,var(--skip) 0 5px,var(--skipline) 5px 7px,var(--skip) 7px 11px);color:#334155}
.byc6-empty{padding:24px;text-align:center;color:var(--muted);font-size:13px;background:var(--soft);border:1px dashed #cbd5e1;border-radius:12px}
.byc6-tip{position:fixed;z-index:9999;display:none;max-width:320px;white-space:pre-line;background:#111827;color:#fff;border-radius:8px;padding:8px 10px;font-size:12px;line-height:1.45;box-shadow:0 10px 24px rgba(15,23,42,.24);pointer-events:none}
.byc6-svg{width:100%;height:230px;display:block;overflow:visible}
.byc6-axis{font-size:10px;fill:#667085;font-weight:700}
.byc6-legend{display:flex;gap:12px;flex-wrap:wrap;font-size:11px;font-weight:700;color:#475467;margin:4px 0 10px}
.byc6-leg{display:flex;align-items:center;gap:5px}
.byc6-dot{width:11px;height:11px;border-radius:3px;display:inline-block}
.byc6-dot.skip{background:repeating-linear-gradient(135deg,var(--skip) 0 5px,var(--skipline) 5px 7px,var(--skip) 7px 11px)}
.byc6-filter-toggle{display:none}
.byc6-drawer-close{display:none}
.byc6-overlay{display:none}
.byc6-mobile-header { display: none; }


.byc6-pie-wrap{display:flex;flex-direction:column;align-items:center;gap:16px}
.byc6-pie{flex-shrink:0}
.byc6-pie-legend{display:flex;flex-direction:row;flex-wrap:wrap;gap:12px;justify-content:center;max-width:100%}
.byc6-pie-leg{display:flex;align-items:center;gap:6px;font-size:12px;font-weight:700;padding:3px 8px;border-radius:6px;cursor:pointer;transition:background .12s;background:#f8fafc;border:1px solid #e5e7eb}
.byc6-pie-leg:hover{background:#f0f5ff}
.byc6-pie-dot{width:12px;height:12px;border-radius:3px;flex-shrink:0}
.byc6-pie-leg-label{color:#374151;white-space:nowrap}
.byc6-pie-leg-val{color:#667085;font-weight:800;white-space:nowrap}
.byc6-pie-total{font-size:12px;font-weight:800;color:#374155;margin-top:8px;padding-top:8px;border-top:1px solid #e5e7eb;text-align:center;width:100%}
@media(max-width:760px){
.byc6-pie-wrap{flex-direction:column;align-items:center}
.byc6-pie-legend{min-width:0;width:100%}
.byc6-shell{grid-template-columns:1fr;position:relative;overflow-x:clip}

  /* proper mobile header row */
  .byc6-mobile-header{
    display:flex;align-items:center;justify-content:space-between;
    padding:10px 12px;border-bottom:1px solid var(--border);background:#fff;
  }
  .byc6-mobile-title{font-size:13px;font-weight:800;color:var(--ink)}

  /* filter toggle — static now, lives inside mobile header */
  .byc6-filter-toggle{
    display:flex;align-items:center;gap:6px;
    position:static;
    background:none;border:1px solid #d1d5db;border-radius:8px;
    padding:6px 10px;font-size:12px;font-weight:800;
    cursor:pointer;font-family:inherit
  }

  /* drawer → bottom sheet anchored above the tab bar */
  .byc6-drawer-close{display:block;margin-bottom:12px;background:none;border:none;cursor:pointer;font-size:13px;font-weight:800;font-family:inherit;text-align:left;padding:0}
  .byc6-shell{position:relative;overflow-x:clip}
  .byc6-left{position:fixed;left:12px;right:12px;bottom:calc(64px + env(safe-area-inset-bottom));top:auto;width:auto;max-height:min(70dvh,460px);z-index:10000;background:#fff;box-shadow:0 12px 32px rgba(0,0,0,.18);border-radius:16px;overflow-y:auto;padding:16px;border:1px solid #e5e7eb;opacity:0;pointer-events:none;transform:translateY(12px);transition:opacity .18s ease,transform .18s ease}
  .byc6-left.open{opacity:1;pointer-events:auto;transform:translateY(0)}
  .byc6-overlay{display:none;position:fixed;inset:0;background:rgba(0,0,0,.22);z-index:9999}
  .byc6-overlay.open{display:block}

  /* main: no padding-top needed anymore */
  .byc6-main{width:100%;padding:0 12px 14px}

  /* view toggle row — static, its own line below header */
  .byc6-head{
    position:static;top:auto;right:auto;
    display:flex;align-items:center;justify-content:flex-end;
    padding:8px 0;margin-bottom:4px;
    border-bottom:1px solid var(--border)
  }
  /* chart title redundant on mobile — context bar says it */
  .byc6-chart-title{display:none}
  .byc6-chart-sub{display:none}
  /* Keep chart-def at mobile-friendly size — desktop 14px is too large on narrow screens */
  .byc6-chart-def{font-size:12px;color:var(--muted);margin-top:4px}

  /* context bar: no margin-top needed anymore */
  .byc6-context{margin-top:8px}

  /* rows */
  .byc6-row{
    grid-template-columns:1fr auto;
    grid-template-areas:"name val" "bar bar";
    gap:4px 8px;padding:12px 0
  }
  .byc6-name{white-space:normal}
  .byc6-val{text-align:right}
  .byc6-bar,.byc6-stack{height:28px}
}

`;

function sn(value, fallback = 0) {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function ratio(a, b) {
  return b > 0 ? a / b : 0;
}

function normalizeArea(area) {
  if (!area) return '';
  return area === 'Verbal Reasoning' ? 'Verbal Ability' : area;
}

function shortArea(area) {
  return AREA_SHORT[area] || area;
}

function typeLabel(type) {
  if (type === ALL_TYPES) return 'All types';
  return `Type ${type}`;
}

function measureLabel(id) {
  return MEASURES.find((m) => m.id === id)?.label || id;
}

function inferGroup(section, area, topic) {
  if (section === ALL_SECTIONS) return 'section';
  if (area === ALL_AREAS) return 'area';
  return 'topic';
}

function makeRowKey(row) {
  return [
    row.test_id ?? row.test_name ?? 'test',
    row.question_id ?? row.qid ?? row.question_no ?? Math.random(),
    row.section,
    row.area,
    row.topic,
    row.type,
  ].join('|');
}

function normalizeQuestionRow(row, idx) {
  const attempted = sn(row.attempted);
  const correct = sn(row.correct);
  const wrong = sn(row.wrong);

  // Validate critical fields — log warnings when data is missing instead of silently using fallbacks
  if (row.total_questions === undefined || row.total_questions === null) {
    console.warn('DataExplorer: row missing total_questions, using 1', { test_name: row.test_name, area: row.area, topic: row.topic });
  }
  if (row.marks_available === undefined || row.marks_available === null) {
    console.warn('DataExplorer: row missing marks_available, using total_questions × 3', { test_name: row.test_name, area: row.area, topic: row.topic });
  }
  if (!row.test_name && !row.test_id) {
    console.warn('DataExplorer: row missing both test_name and test_id, using "Test"', { area: row.area, topic: row.topic });
  }
  if (row.test_order === undefined || row.test_order === null) {
    console.warn('DataExplorer: row missing test_order, using 999', { test_name: row.test_name, test_id: row.test_id, area: row.area, topic: row.topic });
  }

  const total = sn(row.total_questions, 1);
  const skipped = row.skipped === undefined ? Math.max(0, total - attempted) : sn(row.skipped);
  const marksAvailable = sn(row.marks_available, total * 3);
  const rawScore = sn(row.marks_obtained ?? row.score);
  const timeSec = row.time_sec !== undefined ? sn(row.time_sec) : sn(row.time_min) * 60;
  const timeMin = timeSec > 0 ? timeSec / 60 : sn(row.time_min, 0);

  return {
    id: row.id || makeRowKey(row) || `row-${idx}`,
    test_id: row.test_id || null,
    test_name: row.test_name || 'Test',
    test_order: sn(row.test_order, 999),
    is_current: !!row.is_current || sn(row.test_order, 999) === 0,
    section: String(row.section || '').toUpperCase(),
    area: normalizeArea(row.area || ''),
    topic: row.topic || '',
    type: String(row.type || 'All').replace('Type ', '').toUpperCase(),
    total_questions: total,
    attempted,
    correct,
    wrong,
    skipped,
    marks_obtained: rawScore,
    marks_available: marksAvailable,
    time_sec: timeSec,
    time_min: timeMin,
  };
}

function normalizeSectionHistory(row, idx) {
  return {
    id: row.id || `${row.section}-${row.test_id || row.test_name || idx}`,
    section: String(row.section || '').toUpperCase(),
    test_id: row.test_id || null,
    test_name: row.test_name || 'Test',
    test_order: sn(row.test_order, 999),
    is_current: !!row.is_current || sn(row.test_order, 999) === 0,
    percentile: sn(row.percentile, null),
    score: sn(row.score ?? row.marks_obtained, null),
    total_marks: sn(row.total_marks ?? row.marks_available, null),
    cutoff_gap: sn(row.gap_or_buffer_marks ?? row.cutoff_gap, null),
    status: row.status || null,
  };
}

function aggregateRows(rows, groupBy) {
  const map = new Map();

  rows.forEach((r) => {
    const key = groupBy === 'section' ? r.section : groupBy === 'area' ? r.area : r.topic;
    if (!key) return;

    if (!map.has(key)) {
      map.set(key, {
        key,
        label: groupBy === 'area' ? shortArea(key) : key,
        fullLabel: key,
        rows: [],
        total_questions: 0,
        attempted: 0,
        correct: 0,
        wrong: 0,
        skipped: 0,
        marks_obtained: 0,
        marks_available: 0,
        time_sec: 0,
      });
    }

    const g = map.get(key);
    g.rows.push(r);
    g.total_questions += sn(r.total_questions);
    g.attempted += sn(r.attempted);
    g.correct += sn(r.correct);
    g.wrong += sn(r.wrong);
    g.skipped += sn(r.skipped);
    g.marks_obtained += sn(r.marks_obtained);
    g.marks_available += sn(r.marks_available);
    g.time_sec += sn(r.time_sec);
  });

  return Array.from(map.values()).map((g) => {
    const timeMin = g.time_sec > 0 ? g.time_sec / 60 : 0;
    return {
      ...g,
      display_score: Math.max(0, g.marks_obtained),
      marks_left: Math.max(0, g.marks_available - Math.max(0, g.marks_obtained)),
      attempt_rate: ratio(g.attempted, g.total_questions) * 100,
      accuracy: ratio(g.correct, g.correct + g.wrong) * 100,
      conversion: ratio(g.correct, g.total_questions) * 100,
      time_min: timeMin,
      mpm: timeMin > 0 ? g.marks_obtained / timeMin : null,
      leakage: Math.max(0, g.marks_available - g.marks_obtained),
    };
  });
}

function valueFor(row, measure) {
  if (measure === 'score_left') return row.display_score;
  return row[measure];
}

function formatValue(v, measure) {
  if (!Number.isFinite(v)) return '—';
  if (['attempt_rate', 'accuracy', 'conversion'].includes(measure)) return `${v.toFixed(1)}%`;
  if (measure === 'time_min') return `${v.toFixed(1)} min`;
  if (measure === 'mpm') return v.toFixed(2);
  if (measure === 'leakage') return v.toFixed(1);
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function filterQuestionRows(rows, { section, area, topic, type }) {
  return rows.filter((r) => {
    if (section !== ALL_SECTIONS && r.section !== section) return false;
    if (area !== ALL_AREAS && normalizeArea(r.area) !== normalizeArea(area)) return false;
    if (topic !== ALL_TOPICS && r.topic !== topic) return false;
    if (type !== ALL_TYPES && r.type !== type) return false;
    return true;
  });
}

function optionsFromRows(rows, field, filters = {}) {
  const set = new Set();

  rows.forEach((r) => {
    if (filters.section && filters.section !== ALL_SECTIONS && r.section !== filters.section) return;
    if (filters.area && filters.area !== ALL_AREAS && normalizeArea(r.area) !== normalizeArea(filters.area)) return;
    if (filters.topic && filters.topic !== ALL_TOPICS && r.topic !== filters.topic) return;
    const value = field === 'area' ? normalizeArea(r[field]) : r[field];
    if (value) set.add(value);
  });

  return Array.from(set).sort();
}

function sortRows(rows, measure) {
  if (measure === 'cws') return [...rows].sort((a, b) => b.total_questions - a.total_questions);
  return [...rows].sort((a, b) => sn(valueFor(b, measure), -Infinity) - sn(valueFor(a, measure), -Infinity));
}

export default function DataExplorer({ stats }) {
  const chartBuilder = stats?.chart_builder || {};
  const [measure, setMeasure] = useState('attempt_rate');
  const [view, setView] = useState('current');
  const [trendView, setTrendView] = useState('line');
  const [section, setSection] = useState(ALL_SECTIONS);
  const [area, setArea] = useState(ALL_AREAS);
  const [topic, setTopic] = useState(ALL_TOPICS);
  const [type, setType] = useState(ALL_TYPES);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const tipRef = useRef(null);

  const showTip = useCallback((text, e) => {
    const tip = tipRef.current;
    if (!tip) return;
    tip.textContent = text;
    tip.style.display = 'block';
    const rect = tip.getBoundingClientRect();
    const baseX = e.clientX || 0;
    const baseY = e.clientY || 0;
    let x = baseX + 12;
    let y = baseY + 12;
    if (x + rect.width > window.innerWidth - 12) x = baseX - rect.width - 12;
    if (y + rect.height > window.innerHeight - 12) y = baseY - rect.height - 12;
    tip.style.left = `${Math.max(8, x)}px`;
    tip.style.top = `${Math.max(8, y)}px`;
  }, []);

  const hideTip = useCallback(() => {
    if (tipRef.current) tipRef.current.style.display = 'none';
  }, []);

  const questionRows = useMemo(() => {
    const src = view === 'current' ? chartBuilder.current : chartBuilder.trend;
    return Array.isArray(src) ? src.map(normalizeQuestionRow) : [];
  }, [chartBuilder.current, chartBuilder.trend, view]);

  const sectionHistory = useMemo(() => {
    const src = chartBuilder.section_history;
    return Array.isArray(src) ? src.map(normalizeSectionHistory) : [];
  }, [chartBuilder.section_history]);

  const groupBy = inferGroup(section, area, topic);
  const measureObj = MEASURES.find((m) => m.id === measure);

  const sectionOptions = useMemo(() => {
    const sections = optionsFromRows(questionRows, 'section');
    return sections.length ? sections : SECTIONS;
  }, [questionRows]);

  const areaOptions = useMemo(() => {
    if (section === ALL_SECTIONS) return [];
    return optionsFromRows(questionRows, 'area', { section });
  }, [questionRows, section]);

  const topicOptions = useMemo(() => {
    if (section === ALL_SECTIONS || area === ALL_AREAS) return [];
    return optionsFromRows(questionRows, 'topic', { section, area });
  }, [questionRows, section, area]);

  const typeOptions = useMemo(() => {
    const values = optionsFromRows(questionRows, 'type', { section, area, topic })
      .filter((t) => ['A', 'B', 'C'].includes(t));
    return values;
  }, [questionRows, section, area, topic]);

  useEffect(() => {
    if (section === ALL_SECTIONS) {
      setArea(ALL_AREAS);
      setTopic(ALL_TOPICS);
    }
  }, [section]);

  useEffect(() => {
    if (area === ALL_AREAS) setTopic(ALL_TOPICS);
  }, [area]);

  useEffect(() => {
    if (measure === 'percentile') {
      setType(ALL_TYPES);
      if (view === 'current') setView('trend');
    }
  }, [measure]);

  const filteredQuestions = useMemo(() => {
    return filterQuestionRows(questionRows, { section, area, topic, type });
  }, [questionRows, section, area, topic, type]);

  const aggregatedRows = useMemo(() => {
    return sortRows(aggregateRows(filteredQuestions, groupBy), measure).slice(0, 15);
  }, [filteredQuestions, groupBy, measure]);

  const sparklineData = useMemo(() => {
    if (view !== 'trend') return {};
    return buildSparklineData(filteredQuestions, groupBy, measure);
  }, [filteredQuestions, groupBy, measure, view]);

  const percentileRows = useMemo(() => {
    let rows = sectionHistory;
    if (section !== ALL_SECTIONS) rows = rows.filter((r) => r.section === section);
    if (view === 'current') rows = rows.filter((r) => r.is_current);
    return rows;
  }, [sectionHistory, section, view]);

  const pathLabel = [
    section === ALL_SECTIONS ? 'All sections' : section,
    section !== ALL_SECTIONS ? (area === ALL_AREAS ? 'All areas' : shortArea(area)) : null,
    section !== ALL_SECTIONS && area !== ALL_AREAS ? (topic === ALL_TOPICS ? 'All topics' : topic) : null,
  ].filter(Boolean).join(' → ');

  const chartTitle = `${measureLabel(measure)} of ${pathLabel}`;

  const missingBackend = !chartBuilder.current || !chartBuilder.trend || !chartBuilder.section_history;

  return (
    <div className="byc6">
      <style>{css}</style>

      <div className="byc6-shell">
        <div className="byc6-mobile-header">
  <button className="byc6-filter-toggle" onClick={() => setDrawerOpen(true)}>☰ Filters</button>
  <span className="byc6-mobile-title">Data Explorer</span>
</div>
        <div className={`byc6-overlay ${drawerOpen ? 'open' : ''}`} onClick={() => setDrawerOpen(false)} />
        <aside className={`byc6-left ${drawerOpen ? 'open' : ''}`}>
          <button className="byc6-drawer-close" onClick={() => setDrawerOpen(false)}>✕ Close</button>
          <div>
            <div className="byc6-title">Data Explorer</div>
            <div className="byc6-sub">Measure something of sections, areas, or topics.</div>
          </div>

          <div className="byc6-field">
            <label className="byc6-label">Measure</label>
            <select className="byc6-select" value={measure} onChange={(e) => setMeasure(e.target.value)}>
              {MEASURES.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
            <div className="byc6-help">{measureObj?.help}</div>
          </div>

          <div className="byc6-field">
            <label className="byc6-label">Of</label>
            <select className="byc6-select" value={section} onChange={(e) => setSection(e.target.value)}>
              <option value={ALL_SECTIONS}>All sections</option>
              {sectionOptions.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {section !== ALL_SECTIONS && (
            <div className="byc6-field">
              <label className="byc6-label">Area</label>
              <select className="byc6-select" value={area} onChange={(e) => setArea(e.target.value)}>
                <option value={ALL_AREAS}>All {section} areas</option>
                {areaOptions.map((a) => <option key={a} value={a}>{shortArea(a)}</option>)}
              </select>
            </div>
          )}

          <div className="byc6-field">
            <label className="byc6-label">Question type</label>
            <select
              className="byc6-select"
              value={type}
              onChange={(e) => setType(e.target.value)}
              disabled={measure === 'percentile'}
            >
              <option value={ALL_TYPES}>All types</option>
              {typeOptions.map((t) => <option key={t} value={t}>Type {t}</option>)}
            </select>
          </div>
        </aside>

        <main className="byc6-main">
          <div className="byc6-context">
            {measureLabel(measure)} · {section === ALL_SECTIONS
              ? <span>All sections</span>
              : <>{'\u00a0'}<button className="byc6-crumb" onClick={() => setSection(ALL_SECTIONS)}>All sections</button>{'\u00a0'}›{'\u00a0'}{area === ALL_AREAS
                  ? <span>{section}</span>
                  : <>{'\u00a0'}<button className="byc6-crumb" onClick={() => setArea(ALL_AREAS)}>{section}</button>{'\u00a0'}›{'\u00a0'}<span>{shortArea(area)}</span></>}
            </>} · {typeLabel(type)}
          </div>
          <div className="byc6-head">
            <div>
              <div className="byc6-chart-title">{chartTitle}</div>
              <div className="byc6-chart-def">
                {measureDefinition(measure)}
                {view === 'trend' && (
                  <span className="byc6-chart-scope">Showing current test + up to 5 past tests</span>
                )}
              </div>
            </div>
            <div className="byc6-view-toggle">
              <button className={view === 'current' ? 'active' : ''} onClick={() => setView('current')}>Current Test</button>
              <button className={view === 'trend' ? 'active' : ''} onClick={() => setView('trend')}>Trend</button>
            </div>
          </div>

          {missingBackend ? (
            <div className="byc6-empty">
              This data is not available for this report. Try viewing reports generated for newer tests.
            </div>
          ) : aggregatedRows.length === 0 ? (
            <div className="byc6-empty">No chart data available for this selection.</div>
          ) : (view === 'trend' && ['attempt_rate', 'accuracy', 'conversion', 'mpm'].includes(measure)) ? (
            <>
              <div className="byc6-sub-toggle" style={{ marginBottom: 12 }}>
                <button className={trendView === 'line' ? 'active' : ''} onClick={() => setTrendView('line')}>Trendline</button>
                <button className={trendView === 'bar' ? 'active' : ''} onClick={() => setTrendView('bar')}>Cumulative</button>
              </div>
              {(() => {
                const v = trendView === 'line' ? 'trend' : 'current';
                const sd = trendView === 'line' ? sparklineData : null;
                if (measure === 'accuracy') {
                  return <><OutcomeLegend /><AccuracyRows rows={aggregatedRows} showTip={showTip} hideTip={hideTip} view={v} sparklineData={sd} /></>;
                }
                if (measure === 'conversion') {
                  return <><OutcomeLegend /><ConversionRows rows={aggregatedRows} showTip={showTip} hideTip={hideTip} view={v} sparklineData={sd} /></>;
                }
                return <BarRows rows={aggregatedRows} measure={measure} showTip={showTip} hideTip={hideTip} view={v} sparklineData={sd} />;
              })()}
            </>
          ) : measure === 'cws' ? (
            <>
              <OutcomeLegend />
              <StackedRows rows={aggregatedRows} showTip={showTip} hideTip={hideTip} />
            </>
          ) : measure === 'accuracy' ? (
            <>
              <OutcomeLegend />
              <AccuracyRows rows={aggregatedRows} showTip={showTip} hideTip={hideTip} view={view} sparklineData={sparklineData} />
            </>
          ) : measure === 'conversion' ? (
            <>
              <OutcomeLegend />
              <ConversionRows rows={aggregatedRows} showTip={showTip} hideTip={hideTip} view={view} sparklineData={sparklineData} />
            </>
          ) : measure === 'score_left' ? (
            <ScoreLeftRows rows={aggregatedRows} showTip={showTip} hideTip={hideTip} />
          ) : measure === 'time_min' ? (
            <TimeRows rows={aggregatedRows} showTip={showTip} hideTip={hideTip} />
          ) : measure === 'leakage' ? (
            <LeakageRows rows={aggregatedRows} showTip={showTip} hideTip={hideTip} />
          ) : (
            <BarRows rows={aggregatedRows} measure={measure} showTip={showTip} hideTip={hideTip} view={view} sparklineData={sparklineData} />
          )}
        </main>
      </div>
      <div className="byc6-tip" ref={tipRef} />
    </div>
  );
}

function OutcomeLegend() {
  return (
    <div className="byc6-legend">
      <span className="byc6-leg"><span className="byc6-dot" style={{ background: 'var(--green)' }} />Correct</span>
      <span className="byc6-leg"><span className="byc6-dot" style={{ background: 'var(--red)' }} />Wrong</span>
      <span className="byc6-leg"><span className="byc6-dot skip" />Skipped</span>
    </div>
  );
}

function measureDefinition(measure) {
  const defs = {
    attempt_rate: 'How many questions you attempted out of all available questions',
    accuracy: 'Of the questions you attempted, how many you got correct',
    conversion: 'Of all questions (including skipped), how many you got correct',
    cws: 'Breakdown of your outcomes: correct, wrong, and skipped questions',
    score_left: 'Marks you earned versus marks still available to earn',
    leakage: 'Marks you lost due to wrong answers and skipped questions',
    time_min: 'Total time spent on questions in this group',
    mpm: 'How many marks you earned per minute of time spent',
  };
  return defs[measure] || '';
}

function measureColor(measure) {
  if (measure === 'mpm') return 'green';
  return '';
}

function rowTooltip(r, measure) {
  switch (measure) {
    case 'attempt_rate': {
      const attempted = sn(r.attempted);
      const total = sn(r.total_questions);
      const skipped = total - attempted;
      const lines = [r.label, `${attempted}/${total} attempted`];
      if (skipped > 0) lines.push(`${skipped} skipped`);
      lines.push(`Attempt Rate: ${formatValue(r.attempt_rate, 'attempt_rate')}`);
      return lines.join('\n');
    }
    case 'accuracy': {
      const attempted = Math.max(1, sn(r.attempted));
      const correct = sn(r.correct);
      const wrong = sn(r.wrong);
      return [r.label, `${correct} correct of ${attempted} attempted`, `${wrong} wrong`, `Accuracy: ${formatValue(r.accuracy, 'accuracy')}`].join('\n');
    }
    case 'conversion': {
      const total = sn(r.total_questions);
      const correct = sn(r.correct);
      const wrong = sn(r.wrong);
      const skipped = sn(r.skipped);
      return [r.label, `${correct} correct of ${total} total`, `${wrong} wrong, ${skipped} skipped`, `Conversion: ${formatValue(r.conversion, 'conversion')}`].join('\n');
    }
    case 'cws': {
      return [r.label, `${sn(r.total_questions)} questions`, `${sn(r.correct)} Correct · ${sn(r.wrong)} Wrong · ${sn(r.skipped)} Skipped`].join('\n');
    }
    case 'score_left': {
      return [r.label, `Scored: ${sn(r.marks_obtained).toFixed(1)} / ${sn(r.marks_available).toFixed(0)} available`, `Left: ${sn(r.marks_left).toFixed(1)} marks`].join('\n');
    }
    case 'leakage': {
      const wrong = sn(r.wrong);
      const skipped = sn(r.skipped);
      const total = sn(r.total_questions);
      const leakage = sn(r.leakage);
      return [r.label, `Lost: ${leakage.toFixed(1)} marks`, `${wrong} wrong + ${skipped} skipped of ${total}q`].join('\n');
    }
    case 'time_min': {
      const total = sn(r.total_questions);
      const mins = sn(r.time_min);
      const perQ = total > 0 ? mins / total : 0;
      return [r.label, `${mins.toFixed(1)} min total`, `${perQ.toFixed(1)} min/question`].join('\n');
    }
    case 'mpm': {
      const marks = sn(r.marks_obtained);
      const mins = sn(r.time_min);
      return [r.label, `${marks.toFixed(1)} marks in ${mins.toFixed(1)} min`, `${formatValue(r.mpm, 'mpm')} marks/min`].join('\n');
    }
    default:
      return r.label;
  }
}

function trendRowTooltip(r, measure, sparkPoints) {
  if (!sparkPoints || sparkPoints.length === 0) return rowTooltip(r, measure);

  const values = sparkPoints.map((p) => p.value).filter(Number.isFinite);
  if (values.length === 0) return r.label;
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const latest = values[values.length - 1];
  const prev = values.length >= 2 ? values[values.length - 2] : null;
  const latestPt = sparkPoints[sparkPoints.length - 1];

  const lines = [r.label];

  // Metric-specific latest breakdown
  switch (measure) {
    case 'attempt_rate':
      lines.push(`Latest: ${formatValue(latest, measure)} (${sn(latestPt.attempted)}/${sn(latestPt.total_questions)} attempted)`);
      break;
    case 'accuracy':
      lines.push(`Latest: ${formatValue(latest, measure)} (${sn(latestPt.correct)}/${sn(latestPt.correct) + sn(latestPt.wrong)} correct)`);
      break;
    case 'conversion':
      lines.push(`Latest: ${formatValue(latest, measure)} (${sn(latestPt.correct)}/${sn(latestPt.total_questions)} correct)`);
      break;
    case 'mpm':
      lines.push(`Latest: ${formatValue(latest, measure)} (${sn(latestPt.marks_obtained).toFixed(1)} marks in ${sn(latestPt.time_min).toFixed(1)} min)`);
      break;
    default:
      lines.push(`Latest: ${formatValue(latest, measure)}`);
  }

  lines.push(`Avg: ${formatValue(avg, measure)} across ${values.length} tests`);

  const deltaVsAvg = latest - avg;
  const arrow = deltaVsAvg > 0 ? '↑' : deltaVsAvg < 0 ? '↓' : '→';
  lines.push(`vs avg: ${arrow} ${deltaVsAvg >= 0 ? '+' : ''}${deltaVsAvg.toFixed(1)}`);

  if (prev !== null) {
    const deltaVsPrev = latest - prev;
    const arrow2 = deltaVsPrev > 0 ? '↑' : deltaVsPrev < 0 ? '↓' : '→';
    lines.push(`vs previous: ${arrow2} ${deltaVsPrev >= 0 ? '+' : ''}${deltaVsPrev.toFixed(1)}`);
  }

  return lines.join('\n');
}

function pointTooltip(p, measure) {
  const lines = [p.test_name || 'Test'];
  switch (measure) {
    case 'attempt_rate':
      lines.push(`${formatValue(p.value, measure)}`);
      lines.push(`${sn(p.attempted)}/${sn(p.total_questions)} attempted`);
      break;
    case 'accuracy':
      lines.push(`${formatValue(p.value, measure)}`);
      lines.push(`${sn(p.correct)} correct of ${sn(p.correct) + sn(p.wrong)} attempted`);
      break;
    case 'conversion':
      lines.push(`${formatValue(p.value, measure)}`);
      lines.push(`${sn(p.correct)}/${sn(p.total_questions)} correct`);
      break;
    case 'mpm':
      lines.push(`${formatValue(p.value, measure)} marks/min`);
      lines.push(`${sn(p.marks_obtained).toFixed(1)} marks in ${sn(p.time_min).toFixed(1)} min`);
      break;
    default:
      lines.push(formatValue(p.value, measure));
  }
  return lines.join('\n');
}

function buildSparklineData(questionRows, groupBy, measure) {
  const sparklines = {};

  questionRows.forEach((r) => {
    const key = groupBy === 'section' ? r.section : groupBy === 'area' ? r.area : r.topic;
    if (!key) return;

    if (!sparklines[key]) sparklines[key] = {};
    if (!sparklines[key][r.test_order]) {
      sparklines[key][r.test_order] = {
        test_order: r.test_order,
        test_name: r.test_name,
        total_questions: 0,
        attempted: 0,
        correct: 0,
        wrong: 0,
        skipped: 0,
        marks_obtained: 0,
        marks_available: 0,
        time_sec: 0,
      };
    }

    const g = sparklines[key][r.test_order];
    g.total_questions += sn(r.total_questions);
    g.attempted += sn(r.attempted);
    g.correct += sn(r.correct);
    g.wrong += sn(r.wrong);
    g.skipped += sn(r.skipped);
    g.marks_obtained += sn(r.marks_obtained);
    g.marks_available += sn(r.marks_available);
    g.time_sec += sn(r.time_sec);
  });

  const result = {};
  Object.keys(sparklines).forEach((key) => {
    const points = Object.values(sparklines[key])
      .sort((a, b) => b.test_order - a.test_order)
      .map((g) => {
        const timeMin = g.time_sec > 0 ? g.time_sec / 60 : 0;
        let value;
        switch (measure) {
          case 'attempt_rate': value = ratio(g.attempted, g.total_questions) * 100; break;
          case 'accuracy': value = ratio(g.correct, g.correct + g.wrong) * 100; break;
          case 'conversion': value = ratio(g.correct, g.total_questions) * 100; break;
          case 'time_min': value = timeMin; break;
          case 'mpm': value = timeMin > 0 ? g.marks_obtained / timeMin : null; break;
          case 'leakage': value = Math.max(0, g.marks_available - g.marks_obtained); break;
          case 'score_left': value = g.marks_obtained; break;
          default: value = null;
        }
        return {
          test_order: g.test_order,
          test_name: g.test_name,
          value: Number.isFinite(value) ? value : null,
          total_questions: g.total_questions,
          attempted: g.attempted,
          correct: g.correct,
          wrong: g.wrong,
          skipped: g.skipped,
          marks_obtained: g.marks_obtained,
          marks_available: g.marks_available,
          time_min: timeMin,
        };
      })
      .filter((p) => p.value !== null);

    result[key] = points;
  });

  return result;
}

function Sparkline({ points, width = 60, height = 20 }) {
  if (!points || points.length < 2) return <span style={{ display: 'inline-block', width, height, background: '#e5e7eb', borderRadius: 4 }} />;

  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const coords = points.map((p, i) => {
    const x = points.length === 1 ? width / 2 : (i / (points.length - 1)) * (width - 4) + 2;
    const y = height - 2 - ((p.value - min) / range) * (height - 4);
    return { x, y, value: p.value };
  });

  const pathD = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x},${c.y}`).join(' ');

  return (
    <svg width={width} height={height} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <path d={pathD} fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {coords.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r="2.5" fill="#2563eb" />
      ))}
    </svg>
  );
}

function SparklineBar({ points, measure, showTip, hideTip }) {
  if (!points || points.length < 1) {
    return <div className="byc6-bar"><div className="byc6-fill" style={{ width: '0%' }} /></div>;
  }

  const w = 300;
  const h = 28;
  const padX = 8;
  const padY = 5;
  const innerW = w - padX * 2;
  const innerH = h - padY * 2;

  const values = points.map((p) => p.value).filter((v) => Number.isFinite(v));
  if (values.length === 0) return <div className="byc6-bar" />;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const coords = points
    .map((p, i) => {
      if (!Number.isFinite(p.value)) return null;
      const x = points.length === 1 ? w / 2 : padX + (i / (points.length - 1)) * innerW;
      const y = padY + (1 - (p.value - min) / range) * innerH;
      return { x, y, point: p };
    })
    .filter(Boolean);

  const pathD = coords.map((c, i) => `${i === 0 ? 'M' : 'L'}${c.x},${c.y}`).join(' ');

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      style={{ width: '100%', height: '28px', display: 'block' }}
    >
      <path d={pathD} fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {coords.map((c, i) => {
        const dotHandlers = showTip ? {
          onMouseEnter: (e) => { e.stopPropagation(); showTip(pointTooltip(c.point, measure), e); },
          onMouseMove: (e) => { e.stopPropagation(); showTip(pointTooltip(c.point, measure), e); },
          onClick: (e) => { e.stopPropagation(); showTip(pointTooltip(c.point, measure), e); },
          onMouseLeave: (e) => { e.stopPropagation(); hideTip(); },
        } : {};
        return (
          <g key={i}>
            <circle cx={c.x} cy={c.y} r="10" fill="transparent" style={{ cursor: showTip ? 'pointer' : 'default' }} {...dotHandlers} />
            <circle cx={c.x} cy={c.y} r="4" fill="#2563eb" pointerEvents="none" />
          </g>
        );
      })}
    </svg>
  );
}

function trendTooltip(r, measure, sparkPoints) {
  return trendRowTooltip(r, measure, sparkPoints);
}

function trendArrowLabel(sparkPoints) {
  if (!sparkPoints || sparkPoints.length === 0) return null;
  const values = sparkPoints.map((p) => p.value).filter(Number.isFinite);
  if (values.length === 0) return null;
  const avg = values.reduce((a, b) => a + b, 0) / values.length;
  const latest = values[values.length - 1];
  const delta = latest - avg;
  const arrow = delta > 0 ? '↑' : delta < 0 ? '↓' : '→';
  const color = delta > 0 ? '#169b62' : delta < 0 ? '#dc2626' : '#667085';
  const sign = delta > 0 ? '+' : '';
  return <span style={{ color, fontSize: 13, marginLeft: 4 }}>{arrow} {sign}{delta.toFixed(1)}</span>;
}

function BarRows({ rows, measure, showTip, hideTip, view, sparklineData }) {
  const isPercent = ['attempt_rate'].includes(measure);
  const max = isPercent ? 100 : Math.max(...rows.map((r) => Math.abs(sn(valueFor(r, measure)))), 1);
  const colorClass = measureColor(measure);

  return (
    <div>
      {rows.map((r) => {
        const value = valueFor(r, measure);
        const width = Math.max(3, Math.abs(sn(value)) / max * 100);
        const sparkPoints = view === 'trend' ? sparklineData?.[r.key] : null;
        const latestVal = sparkPoints && sparkPoints.length > 0 ? sparkPoints[sparkPoints.length - 1].value : null;
        const tip = sparkPoints ? trendTooltip(r, measure, sparkPoints) : rowTooltip(r, measure);
        const rowHandlers = (!sparkPoints && showTip) ? { onMouseEnter: (e) => showTip(tip, e), onMouseMove: (e) => showTip(tip, e), onMouseLeave: hideTip, onClick: (e) => showTip(tip, e) } : {};
        const labelHandlers = (sparkPoints && showTip) ? { onMouseEnter: (e) => showTip(tip, e), onMouseMove: (e) => showTip(tip, e), onMouseLeave: hideTip, onClick: (e) => showTip(tip, e) } : {};
        const metaText = measure === 'attempt_rate' ? `of ${r.total_questions} questions` : measure === 'time_min' ? `${r.time_min.toFixed(1)} min` : measure === 'mpm' ? `${r.mpm?.toFixed(2) || '—'} marks/min` : measure === 'leakage' ? `${r.wrong + r.skipped} wrong/skipped` : `${r.total_questions}q`;
        const trendArrow = sparkPoints ? trendArrowLabel(sparkPoints) : null;
        return (
          <div key={r.key} className="byc6-row" {...rowHandlers}>
            <div className="byc6-name" {...labelHandlers}>
              {r.label}
              <span className="byc6-meta">{metaText}</span>
            </div>
            {sparkPoints ? (
              <SparklineBar points={sparkPoints} measure={measure} showTip={showTip} hideTip={hideTip} />
            ) : (
              <div className="byc6-bar">
                <div className={`byc6-fill ${sn(value) < 0 ? 'neg' : ''} ${colorClass}`} style={{ width: `${width}%` }} />
              </div>
            )}
            <div className="byc6-val" {...labelHandlers}>
              {sparkPoints ? null : formatValue(value, measure)}
              {trendArrow}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StackedRows({ rows, showTip, hideTip }) {
  return (
    <div>
      {rows.map((r) => {
        const total = Math.max(1, r.correct + r.wrong + r.skipped);
        const tip = rowTooltip(r, 'cws');
        const handlers = showTip ? { onMouseEnter: (e) => showTip(tip, e), onMouseMove: (e) => showTip(tip, e), onMouseLeave: hideTip, onClick: (e) => showTip(tip, e) } : {};
        return (
          <div key={r.key} className="byc6-row" {...handlers}>
            <div className="byc6-name">
              {r.label}
              <span className="byc6-meta">{total}q</span>
            </div>
            <div className="byc6-stack">
              {r.correct > 0 && <div className="byc6-seg byc6-green" style={{ width: `${ratio(r.correct, total) * 100}%` }}>{r.correct}</div>}
              {r.wrong > 0 && <div className="byc6-seg byc6-red" style={{ width: `${ratio(r.wrong, total) * 100}%` }}>{r.wrong}</div>}
              {r.skipped > 0 && <div className="byc6-seg byc6-skip" style={{ width: `${ratio(r.skipped, total) * 100}%` }}>{r.skipped}</div>}
            </div>
            <div className="byc6-val">{r.correct}C {r.wrong}W {r.skipped}S</div>
          </div>
        );
      })}
    </div>
  );
}

function AccuracyRows({ rows, showTip, hideTip, view, sparklineData }) {
  return (
    <div>
      {rows.map((r) => {
        const attempted = Math.max(1, r.attempted);
        const correctPct = ratio(r.correct, attempted) * 100;
        const wrongPct = ratio(r.wrong, attempted) * 100;
        const sparkPoints = view === 'trend' ? sparklineData?.[r.key] : null;
        const latestVal = sparkPoints && sparkPoints.length > 0 ? sparkPoints[sparkPoints.length - 1].value : null;
        const tip = sparkPoints ? trendTooltip(r, 'accuracy', sparkPoints) : rowTooltip(r, 'accuracy');
        const rowHandlers = (!sparkPoints && showTip) ? { onMouseEnter: (e) => showTip(tip, e), onMouseMove: (e) => showTip(tip, e), onMouseLeave: hideTip, onClick: (e) => showTip(tip, e) } : {};
        const labelHandlers = (sparkPoints && showTip) ? { onMouseEnter: (e) => showTip(tip, e), onMouseMove: (e) => showTip(tip, e), onMouseLeave: hideTip, onClick: (e) => showTip(tip, e) } : {};
        const trendArrow = sparkPoints ? trendArrowLabel(sparkPoints) : null;
        return (
          <div key={r.key} className="byc6-row" {...rowHandlers}>
            <div className="byc6-name" {...labelHandlers}>
              {r.label}
              <span className="byc6-meta">of {r.attempted} attempted</span>
            </div>
            {sparkPoints ? (
              <SparklineBar points={sparkPoints} measure="accuracy" showTip={showTip} hideTip={hideTip} />
            ) : (
              <div className="byc6-stack">
                {r.correct > 0 && <div className="byc6-seg byc6-green" style={{ width: `${correctPct}%` }}>{r.correct}</div>}
                {r.wrong > 0 && <div className="byc6-seg byc6-red" style={{ width: `${wrongPct}%` }}>{r.wrong}</div>}
              </div>
            )}
            <div className="byc6-val" {...labelHandlers}>
              {sparkPoints ? null : formatValue(r.accuracy, 'accuracy')}
              {trendArrow}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ConversionRows({ rows, showTip, hideTip, view, sparklineData }) {
  return (
    <div>
      {rows.map((r) => {
        const total = Math.max(1, r.total_questions);
        const correctPct = ratio(r.correct, total) * 100;
        const wrongPct = ratio(r.wrong, total) * 100;
        const skippedPct = ratio(r.skipped, total) * 100;
        const sparkPoints = view === 'trend' ? sparklineData?.[r.key] : null;
        const latestVal = sparkPoints && sparkPoints.length > 0 ? sparkPoints[sparkPoints.length - 1].value : null;
        const tip = sparkPoints ? trendTooltip(r, 'conversion', sparkPoints) : rowTooltip(r, 'conversion');
        const rowHandlers = (!sparkPoints && showTip) ? { onMouseEnter: (e) => showTip(tip, e), onMouseMove: (e) => showTip(tip, e), onMouseLeave: hideTip, onClick: (e) => showTip(tip, e) } : {};
        const labelHandlers = (sparkPoints && showTip) ? { onMouseEnter: (e) => showTip(tip, e), onMouseMove: (e) => showTip(tip, e), onMouseLeave: hideTip, onClick: (e) => showTip(tip, e) } : {};
        const trendArrow = sparkPoints ? trendArrowLabel(sparkPoints) : null;
        return (
          <div key={r.key} className="byc6-row" {...rowHandlers}>
            <div className="byc6-name" {...labelHandlers}>
              {r.label}
              <span className="byc6-meta">of {r.total_questions} total</span>
            </div>
            {sparkPoints ? (
              <SparklineBar points={sparkPoints} measure="conversion" showTip={showTip} hideTip={hideTip} />
            ) : (
              <div className="byc6-stack">
                {r.correct > 0 && <div className="byc6-seg byc6-green" style={{ width: `${correctPct}%` }}>{r.correct}</div>}
                {r.wrong > 0 && <div className="byc6-seg byc6-red" style={{ width: `${wrongPct}%` }}>{r.wrong}</div>}
                {r.skipped > 0 && <div className="byc6-seg byc6-skip" style={{ width: `${skippedPct}%` }}>{r.skipped}</div>}
              </div>
            )}
            <div className="byc6-val" {...labelHandlers}>
              {sparkPoints ? null : formatValue(r.conversion, 'conversion')}
              {trendArrow}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ScoreLeftRows({ rows, showTip, hideTip }) {
  const max = Math.max(...rows.map((r) => sn(r.marks_available)), 1);

  return (
    <div>
      {rows.map((r) => {
        const total = Math.max(1, sn(r.marks_available));
        const width = Math.max(8, total / max * 100);
        const score = sn(r.display_score);
        const isNeg = score < 0;
        const scorePct = ratio(Math.abs(score), total) * 100;
        const leftPct = ratio(Math.max(0, r.marks_left), total) * 100;
        const tip = rowTooltip(r, 'score_left');
        const handlers = showTip ? { onMouseEnter: (e) => showTip(tip, e), onMouseMove: (e) => showTip(tip, e), onMouseLeave: hideTip, onClick: (e) => showTip(tip, e) } : {};

        return (
          <div key={r.key} className="byc6-row" {...handlers}>
            <div className="byc6-name">
              {r.label}
              <span className="byc6-meta">raw score {formatValue(r.marks_obtained, 'score')}</span>
            </div>
            <div className="byc6-stack" style={{ width: `${width}%` }}>
              {isNeg
                ? <div className="byc6-seg byc6-neg" style={{ width: `${scorePct}%` }}>{score}</div>
                : <>
                    {scorePct > 0 && <div className="byc6-seg byc6-score" style={{ width: `${scorePct}%` }}>{score}</div>}
                    {leftPct > 0 && <div className="byc6-seg byc6-leftmarks" style={{ width: `${leftPct}%` }}>{r.marks_left}</div>}
                  </>
              }
            </div>
            <div className="byc6-val">{score}/{r.marks_available}</div>
          </div>
        );
      })}
    </div>
  );
}

const PIE_COLORS = ['#2563eb', '#169b62', '#f97316', '#dc2626', '#8b5cf6', '#0891b2', '#d97706', '#be185d', '#059669', '#4f46e5'];

function TimePieChart({ rows, showTip, hideTip }) {
  const total = rows.reduce((s, r) => s + sn(r.time_min), 0);
  if (total <= 0) return <div className="byc6-empty">No time data available.</div>;

  const size = 200;
  const cx = size / 2;
  const cy = size / 2;
  const r = 85;
  let cumAngle = -Math.PI / 2; // start at top

  const slices = rows.map((row, i) => {
    const mins = sn(row.time_min);
    const pct = mins / total;
    const angle = pct * 2 * Math.PI;
    const startAngle = cumAngle;
    cumAngle += angle;
    const endAngle = cumAngle;

    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const largeArc = angle > Math.PI ? 1 : 0;
    const d = `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;

    const tipText = `${row.label}\n${mins.toFixed(1)} min (${(pct * 100).toFixed(1)}% of total)\n${row.total_questions} questions · ${row.total_questions > 0 ? (mins / row.total_questions).toFixed(1) : '—'} min/question`;
    const handlers = showTip ? { onMouseEnter: (e) => showTip(tipText, e), onMouseMove: (e) => showTip(tipText, e), onMouseLeave: hideTip, onClick: (e) => showTip(tipText, e) } : {};

    return { d, color: PIE_COLORS[i % PIE_COLORS.length], pct, mins, row, handlers };
  });

  return (
    <div className="byc6-pie-wrap">
      <svg className="byc6-pie" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {slices.map((s, i) => (
          <path key={i} d={s.d} fill={s.color} stroke="#fff" strokeWidth="2" {...s.handlers} style={{ cursor: 'pointer' }} />
        ))}
      </svg>
      <div className="byc6-pie-legend">
        {slices.map((s, i) => (
          <div key={i} className="byc6-pie-leg" {...s.handlers}>
            <span className="byc6-pie-dot" style={{ background: s.color }} />
            <span className="byc6-pie-leg-label">{s.row.label}</span>
            <span className="byc6-pie-leg-val">{(s.pct * 100).toFixed(0)}% · {s.mins.toFixed(1)} min</span>
          </div>
        ))}
        <div className="byc6-pie-total">Total: {total.toFixed(1)} min</div>
      </div>
    </div>
  );
}

function TimeBarChart({ rows, showTip, hideTip }) {
  const sorted = [...rows].sort((a, b) => sn(b.time_min) - sn(a.time_min));
  const total = sorted.reduce((s, r) => s + sn(r.time_min), 0);
  const max = Math.max(...sorted.map((r) => sn(r.time_min)), 1);

  return (
    <div>
      {sorted.map((r) => {
        const mins = sn(r.time_min);
        const pct = total > 0 ? (mins / total * 100) : 0;
        const width = Math.max(3, mins / max * 100);
        const tipText = `${r.label}\n${mins.toFixed(1)} min (${pct.toFixed(1)}% of total)\n${r.total_questions} questions · ${r.total_questions > 0 ? (mins / r.total_questions).toFixed(1) : '—'} min/question`;
        const handlers = showTip ? { onMouseEnter: (e) => showTip(tipText, e), onMouseMove: (e) => showTip(tipText, e), onMouseLeave: hideTip, onClick: (e) => showTip(tipText, e) } : {};
        return (
          <div key={r.key} className="byc6-row" {...handlers}>
            <div className="byc6-name">
              {r.label}
              <span className="byc6-meta">{mins.toFixed(1)} min ({pct.toFixed(0)}%)</span>
            </div>
            <div className="byc6-bar">
              <div className="byc6-fill" style={{ width: `${width}%` }} />
            </div>
            <div className="byc6-val">{mins.toFixed(1)} min</div>
          </div>
        );
      })}
      <div style={{ fontSize: '13px', fontWeight: 800, color: '#374155', padding: '8px 0', borderTop: '1px solid #e5e7eb', marginTop: '4px' }}>Total: {total.toFixed(1)} min</div>
    </div>
  );
}

function TimeRows({ rows, showTip, hideTip }) {
  if (rows.length <= 6) {
    return <TimePieChart rows={rows} showTip={showTip} hideTip={hideTip} />;
  }
  return <TimeBarChart rows={rows} showTip={showTip} hideTip={hideTip} />;
}

const LEAKAGE_COLORS = ['#f97316', '#ea580c', '#c2410c', '#9a3412', '#d97706', '#b45309', '#f59e0b', '#d97706'];

function LeakagePieChart({ rows, showTip, hideTip }) {
  const totalLeakage = rows.reduce((s, r) => s + sn(r.leakage), 0);
  const totalAvailable = rows.reduce((s, r) => s + sn(r.marks_available), 0);
  if (totalLeakage <= 0) return <div className="byc6-empty">No leakage — great performance!</div>;

  const size = 200;
  const cx = size / 2;
  const cy = size / 2;
  const r = 85;
  let cumAngle = -Math.PI / 2;

  const slices = rows.map((row, i) => {
    const leak = sn(row.leakage);
    const pct = leak / totalLeakage;
    const angle = pct * 2 * Math.PI;
    const startAngle = cumAngle;
    cumAngle += angle;
    const endAngle = cumAngle;

    // A full-circle slice (angle ≈ 2π) produces identical start/end arc points,
    // which causes the SVG <path> arc to render nothing. Use <circle> instead.
    const isFullCircle = angle >= 2 * Math.PI - 1e-9;

    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const largeArc = angle > Math.PI ? 1 : 0;
    const d = isFullCircle ? '' : `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${largeArc} 1 ${x2},${y2} Z`;

    const tipText = `${row.label}\nLeaked: ${leak.toFixed(1)} marks (${(pct * 100).toFixed(1)}% of total leakage)\n${row.wrong} wrong + ${row.skipped} skipped = ${row.wrong + row.skipped} questions\n${sn(row.marks_available).toFixed(0)} marks available`;
    const handlers = showTip ? { onMouseEnter: (e) => showTip(tipText, e), onMouseMove: (e) => showTip(tipText, e), onMouseLeave: hideTip, onClick: (e) => showTip(tipText, e) } : {};

    return { d, isFullCircle, color: LEAKAGE_COLORS[i % LEAKAGE_COLORS.length], pct, leak, row, handlers };
  });

  return (
    <div className="byc6-pie-wrap">
      <svg className="byc6-pie" width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {slices.map((s, i) => (
          s.isFullCircle
            ? <circle key={i} cx={cx} cy={cy} r={r} fill={s.color} stroke="#fff" strokeWidth="2" {...s.handlers} style={{ cursor: 'pointer' }} />
            : <path key={i} d={s.d} fill={s.color} stroke="#fff" strokeWidth="2" {...s.handlers} style={{ cursor: 'pointer' }} />
        ))}
      </svg>
      <div className="byc6-pie-legend">
        {slices.map((s, i) => (
          <div key={i} className="byc6-pie-leg" {...s.handlers}>
            <span className="byc6-pie-dot" style={{ background: s.color }} />
            <span className="byc6-pie-leg-label">{s.row.label}</span>
            <span className="byc6-pie-leg-val">{s.leak.toFixed(1)} marks ({(s.pct * 100).toFixed(0)}%)</span>
          </div>
        ))}
        <div className="byc6-pie-total">Total leaked: {totalLeakage.toFixed(1)} of {totalAvailable.toFixed(0)} marks</div>
      </div>
    </div>
  );
}

function LeakageBarChart({ rows, showTip, hideTip }) {
  const sorted = [...rows].sort((a, b) => sn(b.leakage) - sn(a.leakage));
  const totalLeakage = sorted.reduce((s, r) => s + sn(r.leakage), 0);
  const totalAvailable = sorted.reduce((s, r) => s + sn(r.marks_available), 0);

  return (
    <div>
      {sorted.map((r) => {
        const leak = sn(r.leakage);
        const available = sn(r.marks_available);
        const pctOfAvailable = available > 0 ? (leak / available * 100) : 0;
        const width = Math.max(3, pctOfAvailable);
        const tipText = `${r.label}\nLeaked: ${leak.toFixed(1)} marks (${pctOfAvailable.toFixed(1)}% of ${available.toFixed(0)} available)\n${r.wrong} wrong + ${r.skipped} skipped = ${r.wrong + r.skipped} questions`;
        const handlers = showTip ? { onMouseEnter: (e) => showTip(tipText, e), onMouseMove: (e) => showTip(tipText, e), onMouseLeave: hideTip, onClick: (e) => showTip(tipText, e) } : {};
        return (
          <div key={r.key} className="byc6-row" {...handlers}>
            <div className="byc6-name">
              {r.label}
              <span className="byc6-meta">{leak.toFixed(1)} marks ({pctOfAvailable.toFixed(0)}% of {available.toFixed(0)})</span>
            </div>
            <div className="byc6-bar">
              <div className="byc6-fill orange" style={{ width: `${width}%` }} />
            </div>
            <div className="byc6-val">{leak.toFixed(1)}</div>
          </div>
        );
      })}
      <div style={{ fontSize: '13px', fontWeight: 800, color: '#374155', padding: '8px 0', borderTop: '1px solid #e5e7eb', marginTop: '4px' }}>Total leaked: {totalLeakage.toFixed(1)} of {totalAvailable.toFixed(0)} marks</div>
    </div>
  );
}

function LeakageRows({ rows, showTip, hideTip }) {
  if (rows.length <= 6) {
    return <LeakagePieChart rows={rows} showTip={showTip} hideTip={hideTip} />;
  }
  return <LeakageBarChart rows={rows} showTip={showTip} hideTip={hideTip} />;
}

function PercentileBars({ rows }) {
  const currentRows = rows.filter((r) => r.is_current);
  if (!currentRows.length) return <div className="byc6-empty">No current percentile data available.</div>;

  return (
    <div>
      {currentRows.sort((a, b) => sn(b.percentile) - sn(a.percentile)).map((r) => (
        <div key={r.id} className="byc6-row">
          <div className="byc6-name">{r.section}</div>
          <div className="byc6-bar">
            <div className="byc6-fill" style={{ width: `${Math.max(3, sn(r.percentile))}%` }} />
          </div>
          <div className="byc6-val">{formatValue(r.percentile, 'percentile')}</div>
        </div>
      ))}
    </div>
  );
}

function PercentileTrend({ rows }) {
  const grouped = new Map();

  rows.forEach((r) => {
    if (!Number.isFinite(r.percentile)) return;
    if (!grouped.has(r.section)) grouped.set(r.section, []);
    grouped.get(r.section).push(r);
  });

  const series = Array.from(grouped.entries()).map(([section, points]) => ({
    section,
    points: points.sort((a, b) => sn(b.test_order) - sn(a.test_order)),
  }));

  if (!series.length) return <div className="byc6-empty">No percentile trend data available.</div>;

  const width = 620;
  const height = 230;
  const pad = 28;
  const colors = ['#2563eb', '#169b62', '#dc2626'];
  const allValues = series.flatMap((s) => s.points.map((p) => p.percentile)).filter(Number.isFinite);
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const range = max - min || 1;

  return (
    <svg className="byc6-svg" viewBox={`0 0 ${width} ${height}`}>
      <line x1={pad} x2={width - pad} y1={height - pad} y2={height - pad} stroke="#e5e7eb" />
      <line x1={pad} x2={pad} y1={pad} y2={height - pad} stroke="#e5e7eb" />

      {series.map((s, idx) => {
        const points = s.points.map((p, i) => {
          const x = s.points.length === 1 ? width / 2 : pad + (i / (s.points.length - 1)) * (width - 2 * pad);
          const y = height - pad - ((p.percentile - min) / range) * (height - 2 * pad);
          return `${x},${y}`;
        }).join(' ');

        const last = s.points[s.points.length - 1];
        const lastY = height - pad - ((last.percentile - min) / range) * (height - 2 * pad);

        return (
          <g key={s.section}>
            <polyline points={points} fill="none" stroke={colors[idx % colors.length]} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            {s.points.map((p, i) => {
              const x = s.points.length === 1 ? width / 2 : pad + (i / (s.points.length - 1)) * (width - 2 * pad);
              const y = height - pad - ((p.percentile - min) / range) * (height - 2 * pad);
              return <circle key={`${s.section}-${i}`} cx={x} cy={y} r="3.5" fill={colors[idx % colors.length]} />;
            })}
            <text className="byc6-axis" x={width - pad + 7} y={lastY + 4}>{s.section}</text>
          </g>
        );
      })}

      <text className="byc6-axis" x={pad} y={height - 8}>Earlier</text>
      <text className="byc6-axis" x={width - 72} y={height - 8}>Latest</text>
    </svg>
  );
}

